/**
 * @author      hugo@zumper.com (Hugo Pineda)
 * @copyright   Copyright (c) 2022, Zumper
 */

// https://chakra-ui.com/docs/styled-system/theming/component-style#styling-single-part-components

import { radioAnatomy } from '@chakra-ui/anatomy'
import type { ComponentMultiStyleConfig } from '@chakra-ui/theme'
import { PartsStyleFunction, PartsStyleObject, mode } from '@chakra-ui/theme-tools'

import { focus } from '../sharedStyles/focus'

const baseStyle: PartsStyleObject<typeof radioAnatomy> = {
  control: {
    borderWidth: 1,
    _focusVisible: focus,
    _focus: { boxShadow: 'none' },
    _checked: {
      _before: {
        width: '60%',
        height: '60%',
      },
    },
  },
  label: {
    fontFamily: 'body',
  },
}

const variantMain: PartsStyleFunction<typeof radioAnatomy> = (props) => ({
  control: {
    background: mode('white', 'white')(props),
    borderWidth: 1,
    borderColor: mode('gray.400', 'gray.400')(props),
    _focusVisible: focus,
    _checked: {
      background: mode('white', 'white')(props),
      borderColor: mode('gray.400', 'gray.400')(props),
      _hover: {
        background: 'transparent',
        borderColor: mode('gray.400', 'gray.400')(props),
      },
      _before: {
        background: mode('pink.600', 'pink.600')(props),
        width: '60%',
        height: '60%',
      },
    },
  },
  label: {
    color: mode('gray.900', 'gray.0')(props),
  },
})

const variantCard: PartsStyleObject<typeof radioAnatomy> = {
  container: {
    _checked: { borderColor: 'gray.800', borderWidth: '1.5px' },
    _disabled: { boxShadow: 'none', cursor: 'default', opacity: 0.7 },
    ':hover:not(:disabled):not(:focus-within)': {
      boxShadow: '0 2px 12px rgba(0, 0, 0, 0.08)',
    },
    _focusWithin: focus,
    _invalid: { borderColor: 'red.500' },
    _last: { mr: 0 },
    alignItems: 'flex-start',
    backgroundColor: 'gray.0',
    borderColor: 'gray.150',
    borderRadius: 'md',
    borderStyle: 'solid',
    borderWidth: '1px',
    boxShadow: '0 2px 4px 0 rgba(0, 0, 0, 0.06)',
    cursor: 'pointer',
    minWidth: '84px',
    mr: 2,
    pb: 3,
    paddingInline: 3,
    position: 'relative',
    scrollSnapAlign: 'start',
    transition: 'all 100ms ease-in',
    userSelect: 'none',
  },
  control: { display: 'none' },
  label: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-end',
    marginInlineStart: 0,
  },
}

const variants = {
  card: variantCard,
  main: variantMain,
}

export const Z4Radio: ComponentMultiStyleConfig = {
  defaultProps: {
    size: 'md',
    variant: 'main',
  },
  baseStyle,
  parts: radioAnatomy.keys,
  variants,
}
